import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
  
const ENV_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const API = `${ENV_API_BASE_URL}/api/home-what-we-offer`;

/* ─── tiny icon map so stored string renders visually ─── */
const ICON_OPTIONS = [
  { value: "FaBook", label: "📖  FaBook" },
  { value: "FaAward", label: "🏆  FaAward" },
  { value: "FaUserGraduate", label: "🎓  FaUserGraduate" },
  { value: "FaHeart", label: "❤️  FaHeart" },
  { value: "FaStar", label: "⭐  FaStar" },
  { value: "FaLeaf", label: "🌿  FaLeaf" },
  { value: "FaMedal", label: "🥇  FaMedal" },
  { value: "FaBrain", label: "🧠  FaBrain" },
];

const EMOJI_MAP = {
  FaBook: "📖",
  FaAward: "🏆",
  FaUserGraduate: "🎓",
  FaHeart: "❤️",
  FaStar: "⭐",
  FaLeaf: "🌿",
  FaMedal: "🥇",
  FaBrain: "🧠",
};

const EMPTY_FORM = {
  icon: "",
  title: "",
  text: "",
  modalTitle: "",
  modalDescription: "",
  features: "",
  image: null,
  order: "",
};

/* ══════════════════════════════════════════════════════
   STYLES  (injected once into <head>)
══════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');

  :root {
    --bg:        #f4f6fb;
    --surface:   #ffffff;
    --surface2:  #f0f2f8;
    --border:    #e2e6f0;
    --border2:   #c8cfe0;
    --accent:    #4f6ef7;
    --accent-dim:#3a56d4;
    --accent-bg: #eef1fe;
    --text:      #1a1d2e;
    --text-muted:#7a80a0;
    --danger:    #e53935;
    --danger-bg: #fdecea;
    --success:   #2e7d52;
    --success-bg:#eaf5ee;
    --warn-bg:   #fff8e1;
    --warn-text: #7a5100;
    --radius:    10px;
    --shadow:    0 2px 16px rgba(79,110,247,0.08);
    --shadow-lg: 0 8px 32px rgba(79,110,247,0.13);
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .wwo-root {
    font-family: 'DM Sans', sans-serif;
    background: var(--bg);
    color: var(--text);
    min-height: 100vh;
    padding: 40px 24px 80px;
  }

  /* ── Header ── */
  .wwo-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 36px;
    padding-bottom: 20px;
    border-bottom: 1px solid var(--border);
  }
  .wwo-header-left h1 {
    font-family: 'Playfair Display', serif;
    font-size: 2rem;
    color: var(--accent);
    letter-spacing: 0.01em;
  }
  .wwo-header-left p {
    color: var(--text-muted);
    font-size: 0.85rem;
    margin-top: 4px;
  }

  /* ── Buttons ── */
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    padding: 9px 20px;
    border: none;
    border-radius: var(--radius);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.18s ease;
    letter-spacing: 0.02em;
  }
  .btn-gold {
    background: var(--accent);
    color: #ffffff;
    box-shadow: 0 2px 8px rgba(79,110,247,0.25);
  }
  .btn-gold:hover { background: var(--accent-dim); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(79,110,247,0.3); }
  .btn-outline {
    background: var(--surface);
    border: 1px solid var(--border2);
    color: var(--text-muted);
  }
  .btn-outline:hover { border-color: var(--accent); color: var(--accent); }
  .btn-warn {
    background: var(--warn-bg);
    color: var(--warn-text);
    border: 1px solid #f5d07a;
  }
  .btn-warn:hover { background: #fff0c0; }
  .btn-danger {
    background: var(--danger-bg);
    color: var(--danger);
    border: 1px solid #ffcdd2;
  }
  .btn-danger:hover { background: var(--danger); color: #fff; }
  .btn-sm { padding: 6px 13px; font-size: 0.8rem; }
  .btn:disabled { opacity: 0.45; cursor: not-allowed; transform: none !important; }

  /* ── Table ── */
  .wwo-table-wrap {
    overflow-x: auto;
    border-radius: var(--radius);
    border: 1px solid var(--border);
    box-shadow: var(--shadow);
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  thead { background: var(--surface2); }
  thead th {
    padding: 14px 16px;
    text-align: left;
    font-weight: 600;
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--text-muted);
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }
  tbody tr {
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    transition: background 0.15s;
  }
  tbody tr:last-child { border-bottom: none; }
  tbody tr:hover { background: var(--surface2); }
  td {
    padding: 12px 16px;
    vertical-align: middle;
    color: var(--text);
  }
  .td-img img {
    width: 56px;
    height: 56px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid var(--border2);
  }
  .td-img .no-img {
    width: 56px;
    height: 56px;
    border-radius: 6px;
    background: var(--surface2);
    border: 1px dashed var(--border2);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.3rem;
  }
  .td-icon { font-size: 1rem; }
  .td-title { font-weight: 500; }
  .td-text {
    max-width: 200px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--text-muted);
  }
  .td-actions { display: flex; gap: 8px; white-space: nowrap; }
  .badge {
    display: inline-block;
    padding: 2px 9px;
    border-radius: 99px;
    font-size: 0.72rem;
    font-weight: 600;
    background: var(--accent-bg);
    color: var(--accent);
    border: 1px solid #c5d0fc;
    letter-spacing: 0.04em;
  }

  /* ── Empty state ── */
  .wwo-empty {
    text-align: center;
    padding: 56px 24px;
    color: var(--text-muted);
  }
  .wwo-empty .empty-icon { font-size: 2.5rem; margin-bottom: 12px; }
  .wwo-empty p { font-size: 0.9rem; }

  /* ── Slide-in form panel ── */
  .wwo-panel-overlay {
    position: fixed;
    inset: 0;
    background: rgba(26,29,46,0.4);
    z-index: 100;
    animation: fadeIn 0.2s ease;
    backdrop-filter: blur(2px);
  }
  .wwo-panel {
    position: fixed;
    top: 0; right: 0;
    width: min(540px, 100vw);
    height: 100vh;
    background: var(--surface);
    border-left: 1px solid var(--border);
    z-index: 101;
    display: flex;
    flex-direction: column;
    box-shadow: -8px 0 40px rgba(79,110,247,0.12);
    animation: slideIn 0.28s cubic-bezier(0.25, 0.8, 0.25, 1);
  }
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to   { transform: translateX(0);    opacity: 1; }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  .wwo-panel-header {
    padding: 22px 24px;
    border-bottom: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    background: var(--surface);
  }
  .wwo-panel-header h2 {
    font-family: 'Playfair Display', serif;
    font-size: 1.2rem;
    color: var(--accent);
  }
  .wwo-panel-header p {
    font-size: 0.78rem;
    color: var(--text-muted);
    margin-top: 2px;
  }
  .close-btn {
    width: 32px; height: 32px;
    border-radius: 50%;
    border: 1px solid var(--border2);
    background: var(--surface2);
    color: var(--text-muted);
    font-size: 1.1rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.15s;
  }
  .close-btn:hover { border-color: var(--accent); color: var(--accent); background: var(--accent-bg); }

  .wwo-panel-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    scrollbar-width: thin;
    scrollbar-color: var(--border2) transparent;
    background: var(--bg);
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* ── Form sections ── */
  .form-section {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: 18px 18px 6px;
  }
  .form-section-title {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--accent);
    font-weight: 700;
    margin-bottom: 16px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--border);
  }
  .form-row { display: flex; gap: 12px; }
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 5px;
    margin-bottom: 14px;
    flex: 1;
  }
  label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--text);
    letter-spacing: 0.01em;
  }
  .required { color: var(--accent); margin-left: 2px; }
  input[type="text"],
  input[type="number"],
  textarea,
  select {
    background: var(--surface);
    border: 1.5px solid var(--border);
    border-radius: var(--radius);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.875rem;
    padding: 9px 12px;
    transition: border-color 0.15s, box-shadow 0.15s;
    width: 100%;
    outline: none;
    resize: vertical;
  }
  input::placeholder, textarea::placeholder { color: var(--text-muted); opacity: 1; }
  input:focus, textarea:focus, select:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px rgba(79,110,247,0.12);
  }
  select option { background: var(--surface); color: var(--text); }
  textarea { min-height: 80px; }

  /* ── Image upload ── */
  .upload-zone {
    border: 2px dashed var(--border);
    border-radius: var(--radius);
    padding: 24px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.18s;
    position: relative;
    background: var(--surface2);
  }
  .upload-zone:hover, .upload-zone.dragover {
    border-color: var(--accent);
    background: var(--accent-bg);
  }
  .upload-zone input[type="file"] {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
  .upload-zone .upload-icon { font-size: 2rem; margin-bottom: 6px; }
  .upload-zone p { font-size: 0.82rem; color: var(--text-muted); }
  .upload-zone .file-name { color: var(--accent); font-size: 0.82rem; margin-top: 6px; font-weight: 500; }

  .img-preview {
    position: relative;
    display: block;
  }
  .img-preview img {
    width: 100%;
    max-height: 160px;
    object-fit: cover;
    border-radius: var(--radius);
    border: 1px solid var(--border2);
  }
  .img-preview .remove-img {
    position: absolute;
    top: 6px; right: 6px;
    background: rgba(255,255,255,0.92);
    border: 1px solid var(--border2);
    color: var(--danger);
    border-radius: 50%;
    width: 26px; height: 26px;
    font-size: 0.75rem;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 1px 4px rgba(0,0,0,0.12);
  }

  /* ── Features tag input ── */
  .features-info {
    font-size: 0.75rem;
    color: var(--text-muted);
    margin-top: 4px;
  }
  .tags-preview {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
  }
  .tag {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 99px;
    font-size: 0.75rem;
    background: var(--accent-bg);
    border: 1px solid #c5d0fc;
    color: var(--accent);
  }

  /* ── Panel footer ── */
  .wwo-panel-footer {
    padding: 16px 24px;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    background: var(--surface);
    box-shadow: 0 -4px 16px rgba(0,0,0,0.04);
  }

  /* ── Toast ── */
  .toast {
    position: fixed;
    bottom: 28px;
    right: 28px;
    padding: 12px 20px;
    border-radius: var(--radius);
    font-size: 0.875rem;
    font-weight: 500;
    z-index: 200;
    animation: toastIn 0.25s ease;
    max-width: 320px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  }
  .toast-success { background: var(--success-bg); border: 1px solid #a8d5b5; color: var(--success); }
  .toast-error   { background: #fff0ef;            border: 1px solid #ffcdd2; color: var(--danger); }
  @keyframes toastIn {
    from { transform: translateY(16px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  /* ── Delete confirm modal ── */
  .confirm-modal {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 200;
    background: rgba(26,29,46,0.45);
    backdrop-filter: blur(4px);
    animation: fadeIn 0.18s ease;
  }
  .confirm-box {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 14px;
    padding: 28px 32px;
    max-width: 380px;
    text-align: center;
    box-shadow: var(--shadow-lg);
  }
  .confirm-box .icon { font-size: 2.5rem; margin-bottom: 14px; }
  .confirm-box h3 { font-family: 'Playfair Display', serif; margin-bottom: 8px; color: var(--text); }
  .confirm-box p { color: var(--text-muted); font-size: 0.875rem; margin-bottom: 22px; }
  .confirm-box .actions { display: flex; gap: 10px; justify-content: center; }

  /* ── Loading ── */
  .spinner {
    display: inline-block;
    width: 18px; height: 18px;
    border: 2px solid rgba(79,110,247,0.2);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  .loading-row td {
    text-align: center;
    padding: 40px;
    color: var(--text-muted);
  }
`;

/* ══════════════════════════════════════════════════════
   COMPONENT
══════════════════════════════════════════════════════ */
export default function HomeWhatWeOffer() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPanel, setShowPanel] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [toast, setToast] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const fileInputRef = useRef();

  /* inject CSS once */
  useEffect(() => {
    const id = "wwo-styles";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.textContent = CSS;
      document.head.appendChild(s);
    }
  }, []);

 // ✅ define first
const fetchData = useCallback(async () => {
  setLoading(true);
  try {
    const res = await axios.get(API);
    setData(res.data.data || []);
  } catch {
    showToast("Failed to load data", "error");
  } finally {
    setLoading(false);
  }
}, []);

// ✅ then use it
useEffect(() => {
  fetchData();
}, [fetchData]);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3200);
  };

  /* ── Form helpers ── */
  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setImagePreview(null);
    setRemoveImage(false);
    setEditId(null);
    setShowPanel(true);
  };

  const openEdit = (item) => {
    setFormData({
      icon: item.icon || "",
      title: item.title || "",
      text: item.text || "",
      modalTitle: item.modalContent?.title || "",
      modalDescription: item.modalContent?.description || "",
      features: item.modalContent?.features?.join(", ") || "",
      image: null,
      order: item.order ?? "",
    });
    setImagePreview(item.image ? `${ENV_API_BASE_URL}/uploads/${item.image}` : null);
    setRemoveImage(false);
    setEditId(item._id);
    setShowPanel(true);
  };

  const closePanel = () => {
    setShowPanel(false);
    setEditId(null);
    setImagePreview(null);
    setRemoveImage(false);
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image" && files[0]) {
      setFormData((p) => ({ ...p, image: files[0] }));
      setImagePreview(URL.createObjectURL(files[0]));
      setRemoveImage(false);
    } else {
      setFormData((p) => ({ ...p, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("icon", formData.icon);
      fd.append("title", formData.title);
      fd.append("text", formData.text);
      fd.append("modalTitle", formData.modalTitle);
      fd.append("modalDescription", formData.modalDescription);
      fd.append("features", formData.features);
      if (formData.order !== "") fd.append("order", formData.order);
      if (formData.image instanceof File) {
        fd.append("image", formData.image);
      } else if (removeImage) {
        fd.append("removeImage", "true");
      }

      if (editId) {
        await axios.put(`${API}/${editId}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("Card updated successfully ✓");
      } else {
        await axios.post(API, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showToast("New card created successfully ✓");
      }
      fetchData();
      closePanel();
    } catch (err) {
      showToast(err?.response?.data?.message || "An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (item) => setDeleteTarget(item);

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/${deleteTarget._id}`);
      showToast("Card deleted");
      fetchData();
    } catch {
      showToast("Delete failed", "error");
    } finally {
      setDeleteTarget(null);
    }
  };

  /* ── Parsed feature tags ── */
  const parsedFeatures = formData.features
    .split(",")
    .map((f) => f.trim())
    .filter(Boolean);

  return (
    <div className="wwo-root">
      {/* ── Header ── */}
      <div className="wwo-header">
        <div className="wwo-header-left">
          <h1>What We Offer</h1>
          <p>Manage cards shown on the homepage offering section</p>
        </div>
        <button className="btn btn-gold" onClick={openCreate}>
          <span>＋</span> Add Card
        </button>
      </div>

      {/* ── Table ── */}
      <div className="wwo-table-wrap">
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Image</th>
              <th>Icon</th>
              <th>Title</th>
              <th>Card Text</th>
              <th>Modal Title</th>
              <th>Order</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="loading-row">
                <td colSpan="8">
                  <div className="spinner" style={{ margin: "0 auto" }} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan="8">
                  <div className="wwo-empty">
                    <div className="empty-icon">🧘</div>
                    <p>No cards yet. Click <strong>Add Card</strong> to get started.</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((item, i) => (
                <tr key={item._id}>
                  <td>{i + 1}</td>
                  <td className="td-img">
                    {item.image ? (
                      <img
                        src={`${ENV_API_BASE_URL}/uploads/${item.image}`}
                        alt={item.title}
                      />
                    ) : (
                      <div className="no-img">🖼️</div>
                    )}
                  </td>
                  <td className="td-icon">
                    {EMOJI_MAP[item.icon] || "❓"}&nbsp;
                    <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>
                      {item.icon}
                    </span>
                  </td>
                  <td className="td-title">{item.title}</td>
                  <td className="td-text">{item.text}</td>
                  <td>
                    <span className="badge">{item.modalContent?.title}</span>
                  </td>
                  <td style={{ color: "var(--text-muted)" }}>{item.order ?? 0}</td>
                  <td>
                    <div className="td-actions">
                      <button
                        className="btn btn-warn btn-sm"
                        onClick={() => openEdit(item)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => confirmDelete(item)}
                      >
                        🗑 Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Slide-in Panel ── */}
      {showPanel && (
        <>
          <div className="wwo-panel-overlay" onClick={closePanel} />
          <div className="wwo-panel">
            <div className="wwo-panel-header">
              <div>
                <h2>{editId ? "Edit Card" : "New Card"}</h2>
                <p>{editId ? "Update the offering card details" : "Fill in details for the new card"}</p>
              </div>
              <button className="close-btn" onClick={closePanel}>✕</button>
            </div>

            <form className="wwo-panel-body" onSubmit={handleSubmit} id="offer-form">

              {/* ── Section 1: Card Details ── */}
              <div className="form-section">
                <div className="form-section-title">Card Details</div>

                {/* Icon + Order row */}
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Icon <span className="required">*</span></label>
                    <select
                      name="icon"
                      value={formData.icon}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select an icon…</option>
                      {ICON_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Order</label>
                    <input
                      type="number"
                      name="order"
                      value={formData.order}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                    />
                  </div>
                </div>

                {/* Heading */}
                <div className="form-group">
                  <label>Heading <span className="required">*</span></label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="e.g. Intensive Format"
                  />
                </div>

                {/* Sub Description */}
                <div className="form-group">
                  <label>Sub Description <span className="required">*</span></label>
                  <textarea
                    name="text"
                    value={formData.text}
                    onChange={handleChange}
                    required
                    placeholder="Short description shown on the card…"
                  />
                </div>
              </div>

              {/* ── Section 2: Modal / Popup Details ── */}
              <div className="form-section">
                <div className="form-section-title">Modal / Popup Details</div>

                {/* Heading */}
                <div className="form-group">
                  <label>Heading <span className="required">*</span></label>
                  <input
                    type="text"
                    name="modalTitle"
                    value={formData.modalTitle}
                    onChange={handleChange}
                    required
                    placeholder="Expanded title shown in the popup"
                  />
                </div>

                {/* Description */}
                <div className="form-group">
                  <label>Description <span className="required">*</span></label>
                  <textarea
                    name="modalDescription"
                    value={formData.modalDescription}
                    onChange={handleChange}
                    required
                    style={{ minHeight: 100 }}
                    placeholder="Full description shown in the popup modal…"
                  />
                </div>

                {/* Key Features in Points */}
                <div className="form-group">
                  <label>Key Features in Points</label>
                  <textarea
                    name="features"
                    value={formData.features}
                    onChange={handleChange}
                    placeholder="Daily practice, Certification, Mentorship…"
                    style={{ minHeight: 68 }}
                  />
                  <span className="features-info">Separate each point with a comma</span>
                  {parsedFeatures.length > 0 && (
                    <div className="tags-preview">
                      {parsedFeatures.map((f, i) => (
                        <span className="tag" key={i}>✓ {f}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Section 3: Card Image ── */}
              <div className="form-section">
                <div className="form-section-title">Card Image</div>

                {imagePreview ? (
                  <div className="img-preview">
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      ref={fileInputRef}
                      style={{ display: "none" }}
                    />
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="remove-img"
                      onClick={() => {
                        setImagePreview(null);
                        setFormData((p) => ({ ...p, image: null }));
                        setRemoveImage(true);
                        if (fileInputRef.current) fileInputRef.current.value = "";
                      }}
                    >✕</button>
                    <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Change Image
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          setImagePreview(null);
                          setFormData((p) => ({ ...p, image: null }));
                          setRemoveImage(true);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                      >
                        Remove Image
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="upload-zone">
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      ref={fileInputRef}
                    />
                    <div className="upload-icon">📷</div>
                    <p>Click or drag an image here</p>
                    <p style={{ fontSize: "0.75rem", marginTop: 4 }}>
                      JPG, PNG, WEBP — max 5 MB
                    </p>
                    {formData.image && (
                      <p className="file-name">{formData.image.name}</p>
                    )}
                  </div>
                )}
              </div>

            </form>

            <div className="wwo-panel-footer">
              <button
                type="submit"
                form="offer-form"
                className="btn btn-gold"
                disabled={saving}
                style={{ flex: 1, justifyContent: "center" }}
              >
                {saving ? <><span className="spinner" /> Saving…</> : editId ? "Update Card" : "Create Card"}
              </button>
              <button className="btn btn-outline" onClick={closePanel} disabled={saving}>
                Cancel
              </button>
            </div>
          </div>
        </>
      )}

      {/* ── Delete confirm ── */}
      {deleteTarget && (
        <div className="confirm-modal">
          <div className="confirm-box">
            <div className="icon">🗑️</div>
            <h3>Delete Card?</h3>
            <p>
              <strong>"{deleteTarget.title}"</strong> will be permanently
              removed along with its image.
            </p>
            <div className="actions">
              <button className="btn btn-danger" onClick={handleDelete}>
                Yes, Delete
              </button>
              <button
                className="btn btn-outline"
                onClick={() => setDeleteTarget(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`toast toast-${toast.type}`}>{toast.msg}</div>
      )}
    </div>
  );
}
